import React, { useState, useEffect, useRef } from 'react';
import { IoIosArrowDown } from "react-icons/io";

const ComboBox1 = ({  
  options,
  findedValue,
  placeholder,
  onSelect,
  onInputChange,
  className,
  inputClassName,
  onKeyDown,
  comboRef,
  onClick,
  onBlur,
  onFocus,
  id,
  inlineStyles,
  inputType = "text",
  readOnly = false,
  disabled = false,
  inputPadding = "2px" // New prop for input padding
}) => {
  const [inputValue, setInputValue] = useState(findedValue || '');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const hasSelected = useRef(false); // Added this ref for better auto-selection handling

  ///////////////////////////selects options automatically//////////////////////
  useEffect(() => {
    setInputValue(findedValue || '');
  }, [findedValue]);

  useEffect(() => {
    if (findedValue && options) {
      const foundOption = options.find(
        (option) => option[1].toString() === findedValue.toString()
      );

      if (foundOption && !hasSelected.current) {
        setInputValue(foundOption[1].toString());
        handleSelectOption(foundOption);
        hasSelected.current = true;
      }
    } else {
      hasSelected.current = false;
    }
  }, [findedValue, options]);

  const clearInput = () => {
    setInputValue('');
  };
  
  // Expose clearInput function through comboRef reference
  useEffect(() => {
    if (comboRef.current) {
      comboRef.current.clearInput = clearInput;
    }
  }, [comboRef]);
  
  useEffect(() => {
    if (!inputValue) {
      setFilteredOptions(options);
    }
  }, [options]);

  const handleInputChange = (event) => {
    const value = event.target.value;

    setInputValue(value);

    // Filter options based on the entered value
    const filtered = options.filter((option) =>
      option.some((optValue) => optValue && optValue.toString().toLowerCase().includes(value.toLowerCase()))
    );
    setFilteredOptions(filtered);
    
    // Call the onInputChange prop with the new input value
    onInputChange && onInputChange(value);

    // Always open the dropdown when there is input
    setIsOpen(value !== '');
  };

  const handleBlur = (event) => {
    setTimeout(() => {
      setIsOpen(false);
    }, 100);

    onBlur && onBlur(event);
  };

  const handleSelectOption = (option) => {
    setInputValue(option[1].toString());
    setIsOpen(false);
    onSelect && onSelect(option);
    onInputChange && onInputChange(option);
  };

  const handleKeyDown = (event) => {
    if (isOpen) {
      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          const currentIndex = filteredOptions.findIndex(option => option[1].toString() === inputValue);
          const nextIndex = (currentIndex + 1) % filteredOptions.length;
          const nextOption = filteredOptions[nextIndex];
          if (nextOption) {
            setInputValue(nextOption[1].toString());
            if (nextIndex >= 0) {
              const dropdownContainer = document.querySelector('.combobox_dropdown');
              const optionElement = document.querySelector(`.dropdown_option:nth-child(${nextIndex + 1})`);
              if (dropdownContainer && optionElement) {
                dropdownContainer.scrollTop = optionElement.offsetTop;
              }
            }
          }
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const currentIndex = filteredOptions.findIndex(option => option[1].toString() === inputValue);
          const prevIndex = (currentIndex - 1 + filteredOptions.length) % filteredOptions.length;
          const prevOption = filteredOptions[prevIndex];
          if (prevOption) {
            setInputValue(prevOption[1].toString());
            const dropdownContainer = document.querySelector('.combobox_dropdown');
            const optionElement = document.querySelector(`.dropdown_option:nth-child(${prevIndex + 1})`);
            if (dropdownContainer && optionElement) {
              dropdownContainer.scrollTop = optionElement.offsetTop;
            }
          }
          break;
        }
        case 'Enter': {
          event.preventDefault();
          const selectedOption = filteredOptions.find(option => option[1].toString() === inputValue);
          if (selectedOption) {
            handleSelectOption(selectedOption);
          }
          setIsOpen(false);
          break;
        }
        case 'Escape':
          setIsOpen(false);
          break;
        default:
          break;
      }
    }
    onKeyDown && onKeyDown(event);
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const inputElement = comboRef.current;

      if (isOpen && inputElement && !inputElement.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isOpen, comboRef]);

  const [isArrowRotated, setIsArrowRotated] = useState(false);

  /////styles - updated with padding and box-sizing////
  const styles = {
    dropdown_option: {
      display: 'flex',
      flexDirection: 'column',
      padding: '8px',
      cursor: 'pointer',
      fontWeight:'600',
    },
    dropdown_optionRow1: {
      display: 'flex',
      width: '100%',
      height: '50%',
    },
    dropdown_optionRow2: {
      display: 'flex',
      width: '100%',
      height: '50%',
    },
    dropdown_optionRow1Col1: {
      width: '50%',
      height: '100%',
    },
    dropdown_optionRow1Col2: {
      width: '50%',
      height: '100%',
      textAlign: 'right',
    },
    dropdown_optionRow2Col1: {
      width: '50%',
      height: '100%',
      color: 'red'
    },
    dropdown_optionRow2Col2: {
      width: '50%',
      height: '100%',
      textAlign: 'right',
    },
    'dropdown_option:hover': {
      backgroundColor: '#f0f0f0',
    },
    combo_box_input: {},
    'combo_box_input_field': {
      cursor: 'pointer',
    },
    'arrow_icon': {
      position: 'absolute',
      top: '50%',
      right: '10px',
      transform: 'translateY(-50%)',
      cursor: 'pointer',
    },
    combo_box_container: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    combo_box_input: {
      position: 'relative',
      width: '100%',
      height: '100%',
    },
    'combo_box_input_field': {
      padding: inputPadding, // Added padding prop
      marginLeft: '0',
      boxSizing: 'border-box', // Added to include padding in dimensions
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      cursor: 'pointer',
      fontFamily: '"Nunito Sans", sans-serif, Roboto, Arial, Helvetica', // Added font family
      outline: 'none',
    },
    'arrow_icon.rotated': {
      transform: 'translateY(-50%) rotate(180deg)',
    },
    combobox_dropdown: {
      width: '100%',
      border: '1px solid #ccc',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      maxHeight: '150px',
      overflowY: 'auto',
      backgroundColor: 'white',
      position: 'relative',
      zIndex: '1000',
    },
    selectedcomboopt: {
      backgroundColor: '#007BFF',
      color: 'white',
    }
  };

  const handleFocus = () => {
    if (readOnly || disabled) return;
    setIsOpen(true);
    onFocus && onFocus();
  };

  return (
    <div className={`combo_box_container ${className}`} style={styles.combo_box_container}>
      <div className="combo_box_input" style={styles.combo_box_input}>
        <input
          id={id}
          ref={comboRef}
          type={inputType}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className={`combo_box_input_field ${inputClassName}`}
          style={{ 
            ...styles.combo_box_input_field, 
            ...inlineStyles,
            // Ensure padding works correctly with box-sizing
            padding: inputPadding,
            boxSizing: 'border-box'
          }}
          onBlur={(e) => handleBlur(e)}
          onFocus={handleFocus}
          onClick={onClick}
          readOnly={readOnly}
          disabled={disabled}
        />

        {isOpen && (
          <div style={styles.combobox_dropdown} className="combobox_dropdown">
            {filteredOptions.map((option, index) => (
              <div
                key={index}
                style={{ 
                  ...styles.dropdown_option, 
                  ...(option[1].toString() === inputValue ? styles.selectedcomboopt : {}) 
                }}
                className={`dropdown_option ${option[1].toString() === inputValue ? 'selectedcomboopt' : ''}`}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleSelectOption(option);
                }}
              >
                <div style={styles.dropdown_optionRow1}>
                  <div style={styles.dropdown_optionRow1Col1}>{option[0]}</div>
                  <div style={styles.dropdown_optionRow1Col2}>{option[1]}</div>
                </div>
                <div style={styles.dropdown_optionRow2}>
                  <div style={styles.dropdown_optionRow2Col1}>{option[2]}</div>
                  <div style={styles.dropdown_optionRow2Col2}>{option[3]}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComboBox1;